import { Box, Camera, Grid3X3, MapPin, Spline, Zap } from "lucide-react";
import { EllipsoidIcon, ExplosionIcon, JointIcon } from "../../ui/Icons";
import { SCENE_GRAPH_NODE_COLORS } from "../../../utils/sceneGraphNodeAppearance";

const ATTACH_TYPE = {
  gyro: "site",
  accelerometer: "site",
  velocimeter: "site",
  force: "site",
  torque: "site",
  touch: "site",
  magnetometer: "site",
  ballquat: "site",
  ballangvel: "site",
  jointpos: "joint",
  jointvel: "joint",
  jointactuatorfrc: "joint",
  jointlimitfrc: "joint",
  actuatorpos: "actuator",
  actuatorvel: "actuator",
  actuatorfrc: "actuator",
  subtreecom: "body",
  subtreelinvel: "body",
  subtreeangmom: "body",
  clock: null,
};

export const ATTACH_ATTR = {
  site: "site",
  joint: "joint",
  actuator: "actuator",
  body: "body",
  tendon: "tendon",
};

export const TYPE_OPTIONS = [
  {
    label: "Site-mounted",
    options: [
      { value: "accelerometer", label: "accelerometer" },
      { value: "gyro", label: "gyro" },
      { value: "velocimeter", label: "velocimeter" },
      { value: "force", label: "force" },
      { value: "torque", label: "torque" },
      { value: "touch", label: "touch" },
      { value: "magnetometer", label: "magnetometer" },
    ],
  },
  {
    label: "Joint",
    options: [
      { value: "jointpos", label: "jointpos" },
      { value: "jointvel", label: "jointvel" },
      { value: "jointactuatorfrc", label: "jointactuatorfrc" },
    ],
  },
  {
    label: "Actuator",
    options: [
      { value: "actuatorpos", label: "actuatorpos" },
      { value: "actuatorvel", label: "actuatorvel" },
      { value: "actuatorfrc", label: "actuatorfrc" },
    ],
  },
  {
    label: "Tendon",
    options: [
      { value: "tendonpos", label: "tendonpos" },
      { value: "tendonvel", label: "tendonvel" },
    ],
  },
  {
    label: "Subtree",
    options: [
      { value: "subtreecom", label: "subtreecom" },
      { value: "subtreelinvel", label: "subtreelinvel" },
      { value: "subtreeangmom", label: "subtreeangmom" },
    ],
  },
  { label: "Other", options: [{ value: "clock", label: "clock" }] },
];

export const CATEGORY_ORDER = ["site", "joint", "actuator", "tendon", "body", "frame", "other"];

export const CATEGORY_LABELS = {
  site: "Site-mounted",
  joint: "Joint",
  actuator: "Actuator",
  tendon: "Tendon",
  body: "Subtree",
  frame: "Frame",
  other: "Other",
};

export function getSensorAttachType(kind) {
  if (kind == null || kind === "") return null;
  if (Object.prototype.hasOwnProperty.call(ATTACH_TYPE, kind)) return ATTACH_TYPE[kind];
  if (typeof kind === "string" && kind.startsWith("tendon")) return "tendon";
  return null;
}

export function getAttachmentLabel(sensor) {
  const at = getSensorAttachType(sensor.kind);
  if (!at) return null;
  const attrKey = ATTACH_ATTR[at];
  if (attrKey) return sensor.attrs?.[attrKey] || null;
  if (sensor.kind?.startsWith("frame")) {
    const t = sensor.attrs?.objtype;
    const n = sensor.attrs?.objname;
    return t && n ? `${t}: ${n}` : null;
  }
  return null;
}

function sceneRefValues(refs) {
  return Object.values(refs || {}).filter(Boolean);
}

function findSceneRefByNameOrUuid(refs, value) {
  if (!refs || !value) return null;
  if (refs[value]) return refs[value];
  return sceneRefValues(refs).find((ref) => ref?.uuid === value || ref?.name === value) ?? null;
}

function getWorldBodyUuid(entry) {
  if (!entry) return null;
  if (typeof entry === "string") return entry;
  return entry.rootBodyUuid || entry.uuid || null;
}

function getWorldBodyName(worldBodies, rootUuid) {
  const entry = (worldBodies || []).find((wb) => getWorldBodyUuid(wb) === rootUuid);
  if (!entry || typeof entry === "string") return null;
  return entry.name || entry.uuid || null;
}

function getBodyRootUuid(bodyUuid, { bodyRootMap }) {
  if (!bodyUuid) return null;
  return bodyRootMap?.[bodyUuid] || bodyUuid;
}

function findBodyForMember(bodies, key, memberUuid) {
  if (!memberUuid) return null;
  return sceneRefValues(bodies).find((body) => {
    const refs = Array.isArray(body?.[key]) ? body[key] : [];
    return refs.some((ref) => ref === memberUuid);
  });
}

function getTargetBodyUuid(attachType, target, sceneRefs) {
  if (!target) return null;
  if (attachType === "body") return target.uuid || null;
  if (attachType === "joint") {
    return findBodyForMember(sceneRefs.bodies, "joints", target.uuid)?.uuid || null;
  }
  if (attachType === "site") {
    return findBodyForMember(sceneRefs.bodies, "sites", target.uuid)?.uuid || null;
  }
  if (attachType === "actuator") {
    const joint = findSceneRefByNameOrUuid(sceneRefs.joints, target.joint);
    if (joint) return getTargetBodyUuid("joint", joint, sceneRefs);
    return null;
  }
  if (attachType === "tendon") {
    const jointRef = Array.isArray(target.joints) ? target.joints[0]?.joint : null;
    const joint = findSceneRefByNameOrUuid(sceneRefs.joints, jointRef);
    if (joint) return getTargetBodyUuid("joint", joint, sceneRefs);
    return null;
  }
  return null;
}

function getTargetInstanceLabel(attachType, target, sceneRefs) {
  const bodyUuid = getTargetBodyUuid(attachType, target, sceneRefs);
  const rootUuid = getBodyRootUuid(bodyUuid, sceneRefs);
  return getWorldBodyName(sceneRefs.worldBodies, rootUuid);
}

function duplicateNameSet(refs) {
  const counts = new Map();
  sceneRefValues(refs).forEach((ref) => {
    if (!ref?.name) return;
    counts.set(ref.name, (counts.get(ref.name) || 0) + 1);
  });
  return new Set(
    Array.from(counts.entries())
      .filter(([, count]) => count > 1)
      .map(([name]) => name)
  );
}

function getRefsForAttachType(attachType, { sites, joints, actuators, bodies, tendons }) {
  if (attachType === "site") return sites;
  if (attachType === "joint") return joints;
  if (attachType === "actuator") return actuators;
  if (attachType === "body") return bodies;
  if (attachType === "tendon") return tendons;
  return {};
}

export function getAttachmentOptions(attachType, sceneRefs) {
  const refs = getRefsForAttachType(attachType, sceneRefs);
  const duplicateNames = duplicateNameSet(refs);
  return sceneRefValues(refs).map((ref) => {
    const instanceLabel = getTargetInstanceLabel(attachType, ref, sceneRefs);
    return {
      value: ref.uuid || ref.name,
      label: ref.name || ref.uuid,
      description: duplicateNames.has(ref.name) && instanceLabel ? instanceLabel : undefined,
    };
  });
}

export function getAttachmentDisplayLabel(attachType, target, sceneRefs) {
  if (!target) return "";
  const duplicateNames = duplicateNameSet(getRefsForAttachType(attachType, sceneRefs));
  const instanceLabel = duplicateNames.has(target.name)
    ? getTargetInstanceLabel(attachType, target, sceneRefs)
    : null;
  const targetLabel = target.name || target.uuid || "";
  return instanceLabel ? `${instanceLabel} / ${targetLabel}` : targetLabel;
}

export function getAttachmentState(sensor, sceneRefs) {
  const attachType = getSensorAttachType(sensor.kind);
  const attrKey = attachType ? ATTACH_ATTR[attachType] : null;
  const value = attrKey ? sensor.attrs?.[attrKey] || "" : "";
  const refs = attachType ? getRefsForAttachType(attachType, sceneRefs) : {};
  const target = attachType ? findSceneRefByNameOrUuid(refs, value) : null;
  const options = attachType ? getAttachmentOptions(attachType, sceneRefs) : [];
  const exists = !value || Boolean(target);
  const displayLabel = target ? getAttachmentDisplayLabel(attachType, target, sceneRefs) : value;
  const selectOptions =
    value && !exists
      ? [{ value, label: `${value} (missing)`, disabled: true }, ...options]
      : value && target && !options.some((option) => option.value === value)
        ? [{ value, label: target.name || value }, ...options]
        : options;

  return {
    attachType,
    attrKey,
    value,
    options: selectOptions,
    missing: Boolean(value && !exists),
    target,
    displayLabel,
  };
}

export function getTargetLegendGlyph(attachType, sensor) {
  let graphKey = attachType ?? null;
  if (!graphKey && sensor?.kind?.startsWith("frame")) {
    const ot = String(sensor.attrs?.objtype ?? "").toLowerCase();
    if (ot.includes("joint")) graphKey = "joint";
    else if (ot.includes("site")) graphKey = "site";
    else if (ot.includes("geom")) graphKey = "geom";
    else if (ot.includes("camera")) graphKey = "camera";
    else if (ot.includes("actuator")) graphKey = "actuator";
    else if (ot.includes("body") || ot.includes("xbody")) graphKey = "body";
  }
  if (!graphKey) graphKey = "body";

  const colors = SCENE_GRAPH_NODE_COLORS[graphKey] ?? SCENE_GRAPH_NODE_COLORS.body;

  let IconComponent;
  let iconCls;
  switch (graphKey) {
    case "site":
    case "attachment_site":
      IconComponent = MapPin;
      iconCls = "w-2.5 h-2.5";
      break;
    case "joint":
      IconComponent = JointIcon;
      iconCls = "w-2 h-2";
      break;
    case "actuator":
      IconComponent = Zap;
      iconCls = "w-2 h-2";
      break;
    case "body":
      IconComponent = Box;
      iconCls = "w-2.5 h-2.5";
      break;
    case "geom":
      IconComponent = ExplosionIcon;
      iconCls = "w-2 h-2";
      break;
    case "mesh":
      IconComponent = Grid3X3;
      iconCls = "w-2 h-2";
      break;
    case "inertial":
      IconComponent = EllipsoidIcon;
      iconCls = "w-2 h-2";
      break;
    case "camera":
      IconComponent = Camera;
      iconCls = "w-2 h-2";
      break;
    case "tendon":
      IconComponent = Spline;
      iconCls = "w-2 h-2";
      break;
    default:
      IconComponent = Box;
      iconCls = "w-2.5 h-2.5";
      break;
  }

  return { IconComponent, iconCls, colors };
}
